import { useCallback, useEffect, useState } from 'react';
import { PropertyField } from '../../../../system/field';
import { sortRecords } from '../utils';
import { useFieldUpdater, ListingResource } from '@brightmls/nextgen.lae.flc';
import { useMetadata } from '../../../../contexts/MetadataContext';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';

type NewFeatureType = {
  key: PropertyField;
  label?: string;
};

const useFeatures = () => {
  const updateFields = useFieldUpdater();
  const { field } = useMetadata();
  const [recommendations, setRecommendations] = useState<any>([]);
  const [useRecommendations, setUseRecommendations] = useState<boolean>(false);
  const [records, setRecords] = useState<any>([]);
  const [features, setFeatures] = useState<any>({});
  const { getFeatureValue } = useFeatureFlags();
  const detectedListingFeatures =
    (getFeatureValue(
      FeatureFlags.NGW_CREATE_DETECTED_LISTING_FEATURES,
    ) as Array<NewFeatureType>) || [];

  const setRecordsAndSort = (records: any) => {
    const sortedRecords = sortRecords(records);
    setRecords(sortedRecords);
  };

  const toggleFeature = useCallback(
    (key: PropertyField) => {
      const isChecked = !features[key].isChecked;
      setFeatures({
        ...features,
        [key]: {
          ...features[key],
          isChecked,
        },
      });
    },
    [features],
  );

  const isFeatureEnabled = useCallback(
    (featureName: PropertyField) => {
      return !!features?.[featureName]?.isChecked;
    },
    [features],
  );

  const updateRecommendationsToState = useCallback(
    (shouldSave?: boolean) => {
      const values = Object.values(detectedListingFeatures);
      const updates: Record<string, any> = {};

      values.forEach((featureName: NewFeatureType) => {
        if (shouldSave && isFeatureEnabled(featureName.key)) {
          updates[featureName.key] = true;
        } else if (!findFeatureInRecommendations({ Key: featureName.key })) {
          updates[featureName.key] = null; // null to remove field
        }
      });

      // Single batch update
      if (Object.keys(updates).length > 0) {
        updateFields(updates);
      }
    },
    [isFeatureEnabled, updateFields],
  );

  const findFeatureInRecommendations = useCallback(
    (fieldMetadata: any) => {
      if (!recommendations.length) return null;
      return recommendations.find(
        (recommendation: any) =>
          fieldMetadata.Key === recommendation.featurename &&
          recommendation.predictedvalue === true,
      );
    },
    [recommendations],
  );

  const findFeatureInRecords = useCallback(
    (fieldMetadata: any) => {
      if (!records.length) return null;
      return records.find(
        (record: any) => record.Data[fieldMetadata.Key] === true,
      );
    },
    [records],
  );

  const getFeatures = useCallback(() => {
    const values = Object.values(detectedListingFeatures);
    const list = values.reduce((list, featureName: NewFeatureType) => {
      const fieldMetadata = field(featureName.key);
      if (!fieldMetadata) return list;
      const featureInRecords = findFeatureInRecords(fieldMetadata);
      const featureInRecom = findFeatureInRecommendations(fieldMetadata);
      const featureFound = featureInRecords || featureInRecom;
      list[featureName.key] = {
        field: fieldMetadata,
        isChecked: !!featureFound,
        key: featureName,
        disabled: false, // Enable features by default
        // record: featureInRecords,
        // recommendation: featureInRecom,
      };
      return list;
    }, {} as any);

    setFeatures(list);
  }, [field, findFeatureInRecords, findFeatureInRecommendations]);

  const render = (renderFn: Function) => {
    const values = Object.values(features);
    if (!values.length) return undefined;
    return values.map((feature: any) => {
      return renderFn(feature);
    });
  };

  useEffect(() => {
    getFeatures();
  }, [records, recommendations, getFeatures]);

  useEffect(() => {
    const updates: Record<string, any> = {};

    detectedListingFeatures.forEach((featureName: NewFeatureType) => {
      updates[featureName.key] = isFeatureEnabled(featureName.key)
        ? true
        : null;
    });

    // Single batch update
    if (Object.keys(updates).length > 0) {
      updateFields(updates);
    }
  }, [features]);

  useEffect(() => {
    updateRecommendationsToState(useRecommendations === false);
  }, [useRecommendations]);

  const listing = {
    getFeatures,
    setRecommendations,
    setRecords: setRecordsAndSort,
    features,
    detectedListingFeatures,
    render,
    toggleFeature,
    useRecommendations: setUseRecommendations,
  };

  return [listing];
};

export default useFeatures;
